/**
 * FlowEmpty - Empty state component when no flows exist
 */

import Button from '@/components/ui/Button';

interface FlowEmptyProps {
  onCreateFlow: () => void;
}

export default function FlowEmpty({ onCreateFlow }: FlowEmptyProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] px-4">
      <div className="bg-gray-50 rounded-full p-6 mb-6">
        <svg
          className="h-16 w-16 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
          />
        </svg>
      </div>

      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        No flows yet
      </h2>

      <p className="text-gray-500 text-center max-w-md mb-6">
        Create your first flow to visualize how money moves through your accounts.
        Sankey diagrams help you understand your income, expenses, and transfers at a glance.
      </p>

      <Button onClick={onCreateFlow} size="lg">
        <svg
          className="h-5 w-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Create Your First Flow
      </Button>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
        <div className="text-center">
          <div className="bg-flow-income/10 rounded-full p-3 w-12 h-12 mx-auto mb-3">
            <svg
              className="h-6 w-6 text-flow-income"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m0-16l-4 4m4-4l4 4"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900">Track Income</h3>
          <p className="text-xs text-gray-500 mt-1">See all money flowing in</p>
        </div>

        <div className="text-center">
          <div className="bg-flow-expense/10 rounded-full p-3 w-12 h-12 mx-auto mb-3">
            <svg
              className="h-6 w-6 text-flow-expense"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 20V4m0 16l4-4m-4 4l-4-4"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900">Analyze Expenses</h3>
          <p className="text-xs text-gray-500 mt-1">Understand spending patterns</p>
        </div>

        <div className="text-center">
          <div className="bg-flow-transfer/10 rounded-full p-3 w-12 h-12 mx-auto mb-3">
            <svg
              className="h-6 w-6 text-flow-transfer"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900">Follow Transfers</h3>
          <p className="text-xs text-gray-500 mt-1">Track money between accounts</p>
        </div>
      </div>
    </div>
  );
}
